/**
 * Security API Service
 *
 * API calls for the Security Monitoring Dashboard.
 * Uses the shared Axios instance from api.ts.
 */

import api from './api';
import type {
  SecurityDashboardResponse,
  SiteDashboardResponse,
  TunnelInfo,
  TunnelMetricPoint,
  TrafficInfo,
  ConnectivityInfo,
  SecurityAlert,
  AlertStatistics,
  AlertRule,
  TimeRange,
  UserSession,
  LoginAttempt,
  AccountLockout,
  AuditLogEntry,
  MfaStatusResponse,
} from '../types/security.types';

export async function fetchSecurityDashboard(): Promise<SecurityDashboardResponse> {
  const res = await api.get<SecurityDashboardResponse>('/v1/security/dashboard');
  return res.data;
}

export async function fetchSiteDashboard(siteId: string): Promise<SiteDashboardResponse> {
  const res = await api.get<SiteDashboardResponse>(`/v1/security/dashboard/${siteId}`);
  return res.data;
}

export async function fetchTunnels(): Promise<{ tunnels: TunnelInfo[] }> {
  const res = await api.get<{ tunnels: TunnelInfo[] }>('/v1/security/tunnels');
  return res.data;
}

export async function fetchTunnelDetail(siteId: string) {
  const res = await api.get(`/v1/security/tunnels/${siteId}`);
  return res.data;
}

export async function fetchTunnelMetrics(
  siteId: string,
  timeRange: TimeRange = '1h'
): Promise<{ site_id: string; time_range: string; metrics: TunnelMetricPoint[] }> {
  const res = await api.get(`/v1/security/tunnels/${siteId}/metrics`, {
    params: { time_range: timeRange },
  });
  return res.data;
}

export async function fetchTrafficOverview(): Promise<{ traffic: TrafficInfo[] }> {
  const res = await api.get<{ traffic: TrafficInfo[] }>('/v1/security/traffic');
  return res.data;
}

export async function fetchSiteTraffic(siteId: string, timeRange: TimeRange = '1h') {
  const res = await api.get(`/v1/security/traffic/${siteId}`, {
    params: { time_range: timeRange },
  });
  return res.data;
}

export async function fetchConnectivity(): Promise<{ connectivity: ConnectivityInfo[] }> {
  const res = await api.get<{ connectivity: ConnectivityInfo[] }>('/v1/security/connectivity');
  return res.data;
}

export async function fetchSecurityAlerts(filters?: {
  severity?: string;
  category?: string;
  site_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ alerts: SecurityAlert[]; total: number }> {
  const res = await api.get<{ alerts: SecurityAlert[]; total: number }>('/v1/security/alerts', {
    params: filters,
  });
  return res.data;
}

export async function fetchAlertStatistics(): Promise<AlertStatistics> {
  const res = await api.get<AlertStatistics>('/v1/security/alerts/statistics');
  return res.data;
}

export async function acknowledgeAlert(alertId: string, notes?: string): Promise<SecurityAlert> {
  const res = await api.post<SecurityAlert>(`/v1/security/alerts/${alertId}/acknowledge`, { notes });
  return res.data;
}

export async function resolveAlert(alertId: string): Promise<SecurityAlert> {
  const res = await api.post<SecurityAlert>(`/v1/security/alerts/${alertId}/resolve`);
  return res.data;
}

// ── Phase B: Auth Monitoring API ─────────────────────────────────────────

export async function fetchSessions(params?: {
  show_all?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ sessions: UserSession[]; total: number }> {
  const res = await api.get<{ sessions: UserSession[]; total: number }>('/v1/security/sessions', {
    params,
  });
  return res.data;
}

export async function terminateSession(sessionId: string, reason?: string): Promise<void> {
  await api.post(`/v1/security/sessions/${sessionId}/terminate`, { reason });
}

export async function fetchLoginAttempts(params?: {
  email?: string;
  result?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
}): Promise<{ attempts: LoginAttempt[]; total: number }> {
  const res = await api.get<{ attempts: LoginAttempt[]; total: number }>('/v1/security/login-attempts', {
    params,
  });
  return res.data;
}

export async function fetchLockouts(params?: {
  limit?: number;
  offset?: number;
}): Promise<{ lockouts: AccountLockout[]; total: number }> {
  const res = await api.get<{ lockouts: AccountLockout[]; total: number }>('/v1/security/lockouts', {
    params,
  });
  return res.data;
}

export async function unlockAccount(lockoutId: string): Promise<void> {
  await api.post(`/v1/security/lockouts/${lockoutId}/unlock`);
}

export async function fetchAuditLog(params?: {
  actor?: string;
  action?: string;
  target_type?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
}): Promise<{ entries: AuditLogEntry[]; total: number }> {
  const res = await api.get<{ entries: AuditLogEntry[]; total: number }>('/v1/security/audit-log', {
    params,
  });
  return res.data;
}

export async function fetchMfaStatus(): Promise<MfaStatusResponse> {
  const res = await api.get<MfaStatusResponse>('/v1/security/mfa-status');
  return res.data;
}

// ── Phase C: Reports, Alert Rules, Site Dashboard ────────────────────────────

export async function generateSecurityReport(params: {
  type: string;
  format: string;
  start?: string;
  end?: string;
}): Promise<Blob> {
  const res = await api.get('/v1/security/reports/generate', {
    params,
    responseType: 'blob',
  });
  return res.data;
}

export async function fetchAlertRules(): Promise<{ rules: AlertRule[] }> {
  const res = await api.get<{ rules: AlertRule[] }>('/v1/security/alert-rules');
  return res.data;
}

export async function updateAlertRule(
  ruleId: string,
  updates: { severity?: string; condition_config?: Record<string, unknown>; is_enabled?: boolean }
): Promise<AlertRule> {
  const res = await api.put<AlertRule>(`/v1/security/alert-rules/${ruleId}`, updates);
  return res.data;
}
